import { useState } from "react";
import { formatDisplayDateTime } from "@carry/shared";
import type { StoredDriverSession } from "../auth";
import { useDriverAttendance, useDriverSchedule } from "../hooks";
import { ROUTE_EDITING_MESSAGE, formatHm, jstDateOffset } from "../lib/normalize";
import {
  DELIVERY_EXECUTION_KINDS,
  OFFLINE_ACTION_LABELS,
  deadLetterReasonText,
  type DeadLetterEntry,
} from "../offline-queue";

function formatDroppedAt(iso: string): string {
  const formatted = formatDisplayDateTime(iso);
  return formatted === "—" ? "" : formatted;
}

export function ApiStatusBanner({
  session,
  schedule,
  attendance,
  acting,
  actionError,
  geoWarning = null,
  pendingQueue,
  deadLetters = [],
  deliveryExecutionAllowed = true,
  routeEditing = false,
  showAttendanceActions = true,
  clockInBlockedReason = null,
  onClockIn,
  onClockOut,
  onFlush,
  onRetryDeadLetter,
  onDiscardDeadLetter,
}: {
  session: StoredDriverSession | null;
  schedule: ReturnType<typeof useDriverSchedule>;
  attendance: ReturnType<typeof useDriverAttendance>;
  acting: boolean;
  actionError: string | null;
  /** F16/F20: 位置情報取得が失敗/拒否されたときの警告 (打刻は記録済み)。 */
  geoWarning?: string | null;
  pendingQueue: number;
  /** Issue #252: flush が破棄したアクション。0 件のときは失敗表示を出さない。 */
  deadLetters?: DeadLetterEntry[];
  /** false の間は配送実行系の再試行を無効化する (安全ゲート、無限再破棄ループ防止)。 */
  deliveryExecutionAllowed?: boolean;
  /** #523: 確定解除で配車担当が編集中のレーンがあるとき true。打刻を止めて案内だけ出す。 */
  routeEditing?: boolean;
  /** false のとき出勤・退勤ボタンを出さない (「明日」タブは閲覧のみ, Issue #338)。 */
  showAttendanceActions?: boolean;
  /**
   * 出勤打刻できない理由 (Issue #338/#548)。非 null のとき出勤ボタンを出さず理由を表示する。
   * 判定は `evaluateClockInGate` に一本化してあり、勤怠 deep link も同じ理由で止まる。
   * 退勤は対象外 — 出勤済みならルートが無くなっても退勤できる必要があるため。
   */
  clockInBlockedReason?: string | null;
  onClockIn: () => void;
  onClockOut: () => void;
  onFlush: () => void;
  onRetryDeadLetter?: (actionId: string) => void;
  onDiscardDeadLetter?: (actionId: string) => void;
}) {
  const [showDeadLetters, setShowDeadLetters] = useState(false);
  if (!session) {
    return (
      <div className="mx-3.5 my-2 rounded-md border border-[var(--warn)] bg-[var(--accent-wash)] px-3 py-2 text-[length:var(--fs-xs)] text-[var(--ink-2)]">
        ○ オフライン表示 (mock データ)
      </div>
    );
  }
  if (schedule.loading && !schedule.routes) {
    return (
      <div className="mx-3.5 my-2 rounded-md border border-[var(--info)] bg-[var(--st-transit-bg)] px-3 py-2 text-[length:var(--fs-xs)] text-[var(--st-transit-ink)]">
        本日のルートを取得中…
      </div>
    );
  }
  if (schedule.error) {
    return (
      <div className="mx-3.5 my-2 flex items-center justify-between gap-2 rounded-md border border-[var(--danger)] bg-[var(--st-cancel-bg)] px-3 py-2 text-[length:var(--fs-xs)] text-[var(--st-cancel-ink)]">
        <span>API 接続エラー: {schedule.error}</span>
        <button
          type="button"
          onClick={() => void schedule.refetch()}
          className="min-h-11 rounded border border-[var(--danger)] bg-[var(--bg-raised)] px-2.5 py-0.5 text-[length:var(--fs-sm)]"
        >
          再試行
        </button>
      </div>
    );
  }
  if (!attendance.loaded || attendance.loading) {
    return (
      <div className="mx-3.5 my-2 rounded-md border border-[var(--info)] bg-[var(--st-transit-bg)] px-3 py-2 text-[length:var(--fs-xs)] text-[var(--st-transit-ink)]">
        勤怠状態を取得中…
      </div>
    );
  }
  if (attendance.error) {
    return (
      <div className="mx-3.5 my-2 flex items-center justify-between gap-2 rounded-md border border-[var(--danger)] bg-[var(--st-cancel-bg)] px-3 py-2 text-[length:var(--fs-xs)] text-[var(--st-cancel-ink)]">
        <span>勤怠状態取得エラー: {attendance.error}</span>
        <button
          type="button"
          onClick={() => void attendance.refetch()}
          className="min-h-11 rounded border border-[var(--danger)] bg-[var(--bg-raised)] px-2.5 py-0.5 text-[length:var(--fs-sm)]"
        >
          再試行
        </button>
      </div>
    );
  }
  const clockedIn = !!attendance.attendance?.clock_in_at;
  const clockedOut = !!attendance.attendance?.clock_out_at;
  const attendanceWorkDate = attendance.attendance?.work_date;
  const isPreviousDayDuty =
    clockedIn && !clockedOut && !!attendanceWorkDate && attendanceWorkDate < jstDateOffset(0);
  const deliveryBlocked = clockedIn && !clockedOut && !attendance.deliverySafety.allowed;
  return (
    <div
      className={`mx-3.5 my-2 rounded-md border px-3 py-2.5 text-[length:var(--fs-xs)] ${
        deliveryBlocked
          ? "border-[var(--danger)] bg-[var(--st-cancel-bg)] text-[var(--st-cancel-ink)]"
          : "border-[var(--ok)] bg-[var(--st-done-bg)] text-[var(--st-done-ink)]"
      }`}
    >
      <div className="flex flex-wrap items-center justify-between gap-2">
        <span className="text-[length:var(--fs-sm)] font-medium">
          {!clockedIn
            ? "本日の出勤打刻"
            : clockedOut
              ? "本日の業務は終了しました"
              : isPreviousDayDuty
                ? "前日から勤務中"
                : "勤務中"}
          {schedule.loading && <span className="ml-1.5 text-[var(--ink-3)]">更新中…</span>}
        </span>
        {!clockedIn && showAttendanceActions && !clockInBlockedReason && (
          <button
            type="button"
            onClick={onClockIn}
            disabled={acting}
            className="flex min-h-12 min-w-[96px] items-center justify-center rounded-md bg-[var(--ok)] px-5 py-2 text-[length:var(--fs-sm)] font-semibold text-white disabled:opacity-60"
          >
            出勤
          </button>
        )}
        {clockedIn &&
          !clockedOut &&
          showAttendanceActions && (
            // 日報導線は「本日の運行」カードの「今日の運転日報を作成」に一本化 (重複解消)。
            <button
              type="button"
              onClick={onClockOut}
              disabled={acting}
              className="flex min-h-12 min-w-[80px] items-center justify-center rounded-md bg-[var(--ink-2)] px-4 py-2 text-[length:var(--fs-sm)] font-semibold text-white disabled:opacity-60"
            >
              退勤
            </button>
          )}
      </div>
      {!clockedIn && showAttendanceActions && clockInBlockedReason && (
        <div className="mt-1 text-[length:var(--fs-xs)] text-[var(--ink-2)]">
          {clockInBlockedReason}
        </div>
      )}
      {clockedIn && (
        <div className="mt-1 text-[length:var(--fs-xs)] text-[var(--st-done-ink)]">
          {isPreviousDayDuty && attendanceWorkDate
            ? `勤務日: ${attendanceWorkDate.replaceAll("-", "/")} · `
            : null}
          出勤: {formatHm(attendance.attendance?.clock_in_at)}
          {clockedOut && <> · 退勤: {formatHm(attendance.attendance?.clock_out_at)}</>}
        </div>
      )}
      {deliveryBlocked && (
        <div role="alert" className="mt-2 font-semibold">
          出勤打刻は記録されましたが、アルコール検査がPASSになるまで配送業務はできません。管理者へ連絡してください。
        </div>
      )}
      {/* #523: 公開済みレーンが確定解除された間だけ出す案内。打刻導線は別途止めている。 */}
      {routeEditing && (
        <div
          role="alert"
          className="mt-2 rounded border border-[var(--warn)] bg-[var(--accent-wash)] px-2.5 py-2 font-semibold text-[var(--ink-2)]"
        >
          {ROUTE_EDITING_MESSAGE}
        </div>
      )}
      {pendingQueue > 0 && (
        <div className="mt-1.5 flex items-center justify-between text-[var(--accent)]">
          <span>⏳ 未送信 {pendingQueue} 件</span>
          <button
            type="button"
            onClick={onFlush}
            className="min-h-11 rounded border border-[var(--accent)] bg-[var(--bg-raised)] px-2.5 py-0.5 text-[length:var(--fs-sm)] text-[var(--accent)]"
          >
            いま再送
          </button>
        </div>
      )}
      {deadLetters.length > 0 && (
        <div role="alert" className="mt-1.5 text-[var(--st-cancel-ink)]">
          <div className="flex items-center justify-between gap-2">
            <span>⚠ 送信に失敗した操作が {deadLetters.length} 件あります</span>
            <button
              type="button"
              onClick={() => setShowDeadLetters((v) => !v)}
              className="min-h-11 rounded border border-[var(--danger)] bg-[var(--bg-raised)] px-2.5 py-0.5 text-[length:var(--fs-sm)] text-[var(--st-cancel-ink)]"
            >
              {showDeadLetters ? "閉じる" : "確認"}
            </button>
          </div>
          {showDeadLetters && (
            <ul className="mt-1.5 space-y-1.5">
              {deadLetters.map((entry) => {
                // 安全ゲート閉鎖中の配送実行系は再送しても再破棄されるだけなので、解除まで無効化する
                const retryBlocked =
                  DELIVERY_EXECUTION_KINDS.includes(entry.action.kind) && !deliveryExecutionAllowed;
                return (
                  <li
                    key={entry.action.id}
                    className="rounded border border-[var(--danger)] bg-[var(--bg-raised)] px-2.5 py-1.5"
                  >
                    <div className="font-medium">
                      {OFFLINE_ACTION_LABELS[entry.action.kind]} ·{" "}
                      {formatDroppedAt(entry.action.enqueuedAt)}
                    </div>
                    <div className="mt-0.5">{deadLetterReasonText(entry)}</div>
                    <div className="mt-1.5 flex gap-2">
                      <button
                        type="button"
                        disabled={retryBlocked}
                        onClick={() => onRetryDeadLetter?.(entry.action.id)}
                        className="min-h-11 rounded border border-[var(--accent)] bg-[var(--bg-raised)] px-2.5 py-0.5 text-[length:var(--fs-sm)] text-[var(--accent)] disabled:opacity-50"
                      >
                        再試行
                      </button>
                      <button
                        type="button"
                        onClick={() => onDiscardDeadLetter?.(entry.action.id)}
                        className="min-h-11 rounded border border-[var(--ink-3)] bg-[var(--bg-raised)] px-2.5 py-0.5 text-[length:var(--fs-sm)] text-[var(--ink-2)]"
                      >
                        破棄
                      </button>
                    </div>
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      )}
      {actionError && <div className="mt-1.5 text-[var(--st-cancel-ink)]">⚠ {actionError}</div>}
      {geoWarning && <div className="mt-1.5 text-[var(--warn)]">⚠ {geoWarning}</div>}
    </div>
  );
}
